import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api, { API, fmtMoney, setMoneyLocale, applyAccent, computeVat, isVatFree, proposalTotals, formatAxiosError } from "../lib/api";
import Logo from "../components/Logo";
import SectionView, { ProposalThemeContext } from "../components/ProposalSections";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { LangContext, makeTr, useTr, dateLocale } from "../lib/publicI18n";
import PrivacyNotice from "../components/PrivacyNotice";
import { Check, Loader2, ChevronDown, ChevronUp, Download, ArrowRight, Lock, ShieldCheck } from "lucide-react";
import { TotalCell, TermsBlock, DocumentsBlock, ProvidersBlock, WithdrawalBlock, WithdrawalFormDialog } from "./PublicProposal";
import GroupSignatureSheet from "./GroupSignatureSheet";

// Villa que le gestionnaire peut cocher : encore à signer (une « refusée » reste signable, mais n'est pas cochée d'office).
const selectable = (p) => p.group_state === "signable" || p.group_state === "refusée";

/** Le dossier d'une villa signée reste à finaliser (pièces, mandat SEPA, choix des chaînes) : sa page individuelle porte la suite. */
const needsFinalize = (p) => !!p.dossier && !p.dossier.complete
  && ((p.dossier.documents?.missing?.length || 0) > 0 || (p.dossier.sepa?.required && !p.dossier.sepa?.done) || (p.dossier.channels?.required && !p.dossier.channels?.done));

function StateBadge({ state }) {
  const tr = useTr();
  const labels = {
    signable: [tr("À signer", "To sign"), "bg-primary/10 text-primary"],
    "refusée": [tr("Refusée", "Declined"), "bg-amber-50 text-amber-700"],
    "signée": [tr("Signée", "Signed"), "bg-emerald-50 text-emerald-700"],
    "expirée": [tr("Expirée", "Expired"), "bg-rose-50 text-rose-700"],
    brouillon: [tr("Pas encore envoyée", "Not sent yet"), "bg-muted text-muted-foreground"],
  };
  const [text, cls] = labels[state] || labels.signable;
  return <span className={`text-[11px] font-bold uppercase tracking-wide px-2 py-1 rounded-sm shrink-0 ${cls}`}>{text}</span>;
}

/** Page publique d'un dossier groupé : les villas d'un même client, signées ensemble (une, plusieurs ou toutes). Voir la spec « dossier groupé », §4 et §8. */
export default function PublicProposalGroup() {
  const { groupId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null); // null | "notfound" (lien inconnu ou dossier non envoyé : 404) | "unavailable" (panne passagère : 5xx, 429, réseau)
  // Cases cochées : par défaut les villas « à signer » (une « refusée » reste signable mais n'est pas cochée d'office) ; `overrides` ne retient que les choix du gestionnaire.
  const [overrides, setOverrides] = useState({});
  const [opened, setOpened] = useState({});
  const [sheetOpen, setSheetOpen] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [justSigned, setJustSigned] = useState(0);
  const [withdrawalOpen, setWithdrawalOpen] = useState(false);
  const tracked = useRef(false);
  // Visiteur INTERNE (utilisateur Acteio connecté) : jamais compté comme une ouverture du client.
  const isInternalViewer = typeof window !== "undefined" && !!localStorage.getItem("acteio_token");

  useEffect(() => () => setMoneyLocale("fr"), []); // montants du back-office : jamais laissés en anglais

  const apply = (d) => {
    setMoneyLocale(d?.group?.language);
    setData(d);
    if (d?.brand?.accent_hsl) applyAccent(d.brand.accent_hsl);
  };
  const loadFirst = () => {
    setErr(null);
    api.get(`/public/groups/${groupId}`).then((r) => apply(r.data)).catch((e) => setErr(e?.response?.status === 404 ? "notfound" : "unavailable"));
  };
  useEffect(() => { loadFirst(); }, [groupId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Rechargement silencieux (pièce déposée, 409 après une signature) : un échec transitoire ne fait jamais disparaître le dossier affiché.
  const reload = async () => {
    try {
      const { data: d } = await api.get(`/public/groups/${groupId}`);
      apply(d);
    } catch (e) { /* on garde l'état affiché */ }
  };

  useEffect(() => {
    if (!data || data.group?.cancelled || isInternalViewer || tracked.current) return;
    tracked.current = true;
    api.post(`/public/groups/${groupId}/track`, { type: "open" }).catch(() => {});
  }, [data, groupId, isInternalViewer]);

  const lang = data?.group?.language === "en" ? "en" : "fr";
  const tr = makeTr(lang);
  useDocumentTitle(data?.group?.title ? `${data.group.title}${data.brand?.name ? ` · ${data.brand.name}` : ""}` : "", false);

  const proposals = data?.proposals;
  const isChecked = (p) => selectable(p) && (overrides[p.public_id] ?? p.group_state === "signable");
  const chosen = useMemo(() => (proposals || []).filter(isChecked), [proposals, overrides]); // eslint-disable-line react-hooks/exhaustive-deps
  const totals = useMemo(() => chosen.reduce((acc, p) => {
    const t = proposalTotals(p.sections);
    return { oneShot: acc.oneShot + t.oneShot, recurring: acc.recurring + t.recurring };
  }, { oneShot: 0, recurring: 0 }), [chosen]);

  // Pièces communes : une ligne par type exigé par les villas cochées ; « déposée » si TOUTES celles qui l'exigent l'ont (un seul dépôt les couvre).
  const docRows = useMemo(() => {
    const byType = new Map();
    chosen.forEach((p) => (p.required_documents || []).forEach((rd) => {
      const row = byType.get(rd.type) || { rd, villas: [] };
      row.villas.push(p);
      byType.set(rd.type, row);
    }));
    const required = [...byType.values()].map((r) => r.rd);
    const present = [...byType.values()].filter((r) => r.villas.every((p) => (p.documents || []).some((d) => d.type === r.rd.type)))
      .map((r) => (r.villas[0].documents || []).find((d) => d.type === r.rd.type));
    return { required, present };
  }, [chosen]);

  if (err === "notfound") {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground p-6 text-center" data-testid="group-not-found">
        Dossier introuvable. <span lang="en" className="ml-1">File not found.</span>
      </div>
    );
  }
  // Une panne passagère n'est pas un lien mort : le gestionnaire doit pouvoir réessayer (la langue du dossier n'est pas encore connue : les deux).
  if (err === "unavailable") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-muted-foreground p-6 text-center" data-testid="group-unavailable">
        <p>Chargement impossible, réessayez dans un instant. <span lang="en" className="block mt-1">Unable to load: please try again in a moment.</span></p>
        <button type="button" onClick={loadFirst} data-testid="group-retry" className="min-h-11 px-5 rounded-sm bg-primary text-white font-semibold hover:bg-primary/90 transition-colors">
          Réessayer <span lang="en">/ Try again</span>
        </button>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const brandObj = { name: data.brand?.name || "", logo_letter: data.brand?.logo_letter || "", logo_file_id: data.brand?.logo_file_id };
  const brandName = data.brand?.name || "";
  const premium = data.brand?.public_theme === "premium";
  const header = (
    <header className={premium
      ? "h-16 bg-[#ECE9E3]/90 backdrop-blur-xl sticky top-0 z-40 flex items-center px-5 sm:px-8"
      : "h-16 bg-white/80 backdrop-blur-xl border-b border-border/60 sticky top-0 z-40 flex items-center px-4 sm:px-6"}>
      <Logo brand={brandObj} />
    </header>
  );

  if (data.group?.cancelled) {
    return (
      <LangContext.Provider value={lang}>
        <div className="min-h-screen bg-surface">
          {header}
          <div className="max-w-xl mx-auto px-4 py-16 text-center space-y-3" data-testid="group-cancelled">
            <Lock className="w-8 h-8 mx-auto text-muted-foreground" />
            <h1 className="font-heading text-2xl font-bold">{data.group.title}</h1>
            <p className="text-muted-foreground">
              {tr("Ce dossier a été annulé : il ne peut plus être consulté ni signé.", "This file has been cancelled: it can no longer be viewed or signed.")}
              {" "}{tr(`Contactez ${brandName || "votre interlocuteur"} pour en savoir plus.`, `Please contact ${brandName || "your advisor"} for more information.`)}
            </p>
            <PrivacyNotice companyName={brandName} testIdSuffix="-group" className="text-xs underline decoration-dotted underline-offset-2 text-muted-foreground hover:text-foreground transition-colors" />
          </div>
        </div>
      </LangContext.Provider>
    );
  }

  const members = proposals || [];
  const currency = members[0]?.currency || "EUR";
  const vatRate = data.brand?.vat_rate;
  const vatFree = isVatFree(vatRate);
  const HT = vatFree ? "" : tr("HT", "excl. VAT");
  const signedCount = data.group.signed_count || 0;
  const allSigned = members.length > 0 && signedCount === members.length;
  const canSign = members.some(selectable);
  const bannerCount = justSigned || (allSigned ? members.length : 0);
  const count = members.length;

  const toggle = (p) => setOverrides((cur) => ({ ...cur, [p.public_id]: !isChecked(p) }));

  const sign = async (body) => {
    setDeciding(true);
    try {
      const { data: res } = await api.post(`/public/groups/${groupId}/decision`, body);
      setSheetOpen(false);
      setJustSigned(res?.signed?.length || body.public_ids.length);
      // Sans attendre la relecture : les villas signées ne restent pas cochées (ni proposées) si elle tarde ou échoue.
      setOverrides((cur) => ({ ...cur, ...Object.fromEntries(body.public_ids.map((id) => [id, false])) }));
      await reload();
      return { ok: true };
    } catch (e) {
      const d = e.response?.data;
      const status = e.response?.status;
      // Le dossier a changé depuis l'affichage (régénération, annulation…) : on le relit avant de rendre la main, pour resynchroniser.
      if (status === 409) await reload();
      // Une erreur PAR VILLA : par adresse publique (`villas`) ou par identifiant interne (`faults`) — affichée sous la villa fautive ; une faute
      // du DOSSIER (sans villa : numéro en double, acompte…) reste dite en général, même quand des villas sont aussi désignées.
      const byVilla = {};
      const general = [];
      (d?.villas || []).forEach((v) => { byVilla[v.public_id] = v.message; });
      (d?.faults || []).forEach((f) => {
        const m = members.find((x) => x.id === f.proposal_id);
        if (m) byVilla[m.public_id] = [byVilla[m.public_id], f.message].filter(Boolean).join(" ");
        else general.push(f.message);
      });
      const flagged = Object.keys(byVilla).length > 0;
      return {
        ok: false, status, villas: byVilla, general: general.join(" ; "),
        message: flagged ? "" : formatAxiosError(e),
      };
    } finally { setDeciding(false); }
  };

  // Prestataires tiers : UNE mention par (prestataire, documents) pour tout le dossier — un même prestataire aux documents différents garde sa mention par version.
  const providerList = (() => {
    const seen = new Map();
    members.filter((p) => p.group_state !== "signée").forEach((p) => (p.providers || []).forEach((pr) => {
      const key = `${pr.id}|${JSON.stringify((pr.documents || []).map((d) => [d.file_id || null, d.url || null, d.sha256 || null]).sort())}`;
      const cur = seen.get(key);
      if (cur) cur.lines = [...new Set([...cur.lines, ...pr.lines])];
      else seen.set(key, { ...pr, id: [...seen.values()].some((x) => x.id === pr.id) ? `${pr.id}-${seen.size}` : pr.id, lines: [...pr.lines] });
    }));
    return [...seen.values()];
  })();
  // Droit de rétractation (client particulier ou petit professionnel) : UNE mention, avec le formulaire type de la première villa concernée.
  const consumerLegal = members.find((p) => p.group_state !== "signée" && p.legal?.consumer_like)?.legal || null;
  const card = premium ? "rounded-[20px] bg-white" : "bg-white border border-border shadow-sm rounded-sm";
  const terms = data.terms || [];
  const firstUsing = (t) => members.find((p) => p.terms?.id === t.id);
  const amountsLine = [totals.recurring > 0 && `${fmtMoney(totals.recurring, currency)} ${HT} ${tr("/ mois", "/ month")}`, totals.oneShot > 0 && `${fmtMoney(totals.oneShot, currency)} ${HT}`]
    .filter(Boolean).map((s) => s.replace(/\s+/g, " ").trim()).join(" · ");
  const n = chosen.length;
  const totalCells = [
    totals.oneShot > 0 && {
      id: "one-shot", amount: totals.oneShot, vat: vatFree ? null : computeVat(totals.oneShot, vatRate),
      label: totals.recurring > 0 ? tr("Montant unique", "One-off amount") : tr("Montant total", "Total amount"),
      testId: "public-total-one-shot", testIdVat: "public-vat-block-one-shot", testIdTtc: "public-total-ttc-one-shot",
    },
    totals.recurring > 0 && {
      id: "recurring", amount: totals.recurring, vat: vatFree ? null : computeVat(totals.recurring, vatRate), monthly: true,
      label: tr("Montant mensuel", "Monthly amount"),
      testId: "public-total-recurring", testIdVat: "public-vat-block-recurring", testIdTtc: "public-total-ttc-recurring",
    },
  ].filter(Boolean);

  return (
    <LangContext.Provider value={lang}>
      <ProposalThemeContext.Provider value={premium ? "premium" : "classic"}>
        <div className={premium ? "proposal-premium min-h-screen pb-40 break-words" : "min-h-screen bg-surface pb-40 break-words"} data-testid="group-page">
          {header}
          <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
            <div>
              <p className="text-[12px] font-bold uppercase tracking-[0.06em] text-primary">{tr("Dossier", "File")}</p>
              <h1 className={premium ? "font-display font-extrabold text-[2rem] sm:text-4xl leading-[1.05] tracking-[-0.02em] mt-1" : "font-heading text-3xl font-bold mt-1"} data-testid="group-title">
                {data.group.title}
              </h1>
              <p className="text-muted-foreground mt-2" data-testid="group-summary">
                {count} {count > 1 ? tr("propositions", "proposals") : tr("proposition", "proposal")}
                {signedCount > 0 && ` · ${signedCount} ${signedCount > 1 ? tr("signées", "signed") : tr("signée", "signed")}`}
                {canSign && ` — ${tr("signez-en une, plusieurs ou toutes d'un seul geste.", "sign one, several or all of them in one go.")}`}
              </p>
            </div>

            {bannerCount > 0 && (
              <div className="rounded-sm border-2 border-emerald-200 bg-emerald-50 text-emerald-900 p-4 flex items-start gap-3" data-testid="group-signed-banner">
                <Check className="w-5 h-5 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold">{bannerCount} {bannerCount > 1 ? tr("contrats signés", "contracts signed") : tr("contrat signé", "contract signed")}</p>
                  <p className="text-sm">{tr("Vous recevez par email vos exemplaires signés. Il reste, villa par villa, à finaliser ce qui est indiqué ci-dessous.", "Your signed copies are sent to you by email. For each property, the remaining steps are listed below.")}</p>
                </div>
              </div>
            )}

            <ul className="space-y-3">
              {members.map((p) => {
                const pid = p.public_id;
                const t = proposalTotals(p.sections);
                const isOpen = !!opened[pid];
                const signedAt = p.signature?.signed_at ? new Date(p.signature.signed_at) : null;
                const services = [...new Set((p.sections || []).filter((s) => s.type === "pricing" || s.type === "configurable")
                  .flatMap((s) => s.items || []).filter((it) => !it.requires && it.name).map((it) => it.name))].slice(0, 4);
                return (
                  <li key={pid} className={`${card} p-4 sm:p-5`} data-testid={`group-villa-${pid}`}>
                    <div className="flex items-start gap-3">
                      {/* Cible tactile de 44 px autour de la case de 22 px. */}
                      <label className="-ml-2.5 -mt-2 w-11 h-11 shrink-0 flex items-center justify-center cursor-pointer">
                        <input type="checkbox" checked={isChecked(p)} disabled={!selectable(p)} onChange={() => toggle(p)} data-testid={`group-select-${pid}`}
                          aria-label={tr(`Inclure ${p.title} dans la signature`, `Include ${p.title} in the signature`)}
                          className="w-[22px] h-[22px] disabled:opacity-40" style={{ accentColor: "hsl(var(--primary))" }} />
                      </label>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h2 className="font-heading font-bold text-lg">{p.title}</h2>
                          <span data-testid={`group-state-${pid}`}><StateBadge state={p.group_state} /></span>
                        </div>
                        {services.length > 0 && <p className="text-sm text-muted-foreground mt-0.5">{services.join(", ")}</p>}
                        <p className="mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-0.5">
                          {t.recurring > 0 && (
                            <span className="font-semibold" data-testid={`group-villa-monthly-${pid}`}>
                              {fmtMoney(t.recurring, p.currency)}{HT && <span className="text-xs text-muted-foreground ml-1">{HT}</span>}<span className="text-xs text-muted-foreground ml-1">{tr("/ mois", "/ month")}</span>
                            </span>
                          )}
                          {t.oneShot > 0 && (
                            <span className="text-sm text-muted-foreground" data-testid={`group-villa-once-${pid}`}>
                              {fmtMoney(t.oneShot, p.currency)}{HT && <span className="text-xs ml-1">{HT}</span>} {t.recurring > 0 ? tr("une seule fois", "one-off") : ""}
                            </span>
                          )}
                        </p>
                        {p.group_state === "signée" && (
                          <p className="text-sm mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                            {signedAt && <span>{tr("Signée le", "Signed on")} {new Intl.DateTimeFormat(dateLocale(lang), { dateStyle: "long" }).format(signedAt)}</span>}
                            {needsFinalize(p) && <span className="text-[11px] font-bold uppercase tracking-wide px-2 py-1 rounded-sm bg-amber-50 text-amber-700" data-testid={`group-finalize-${pid}`}>{tr("Dossier à finaliser", "File to complete")}</span>}
                            <a href={`/p/${pid}#dossier`} data-testid={`group-villa-next-${pid}`} className="inline-flex items-center gap-1 font-semibold text-primary hover:underline min-h-11 sm:min-h-0">
                              {needsFinalize(p) ? tr("Finaliser le dossier", "Complete the file") : tr("Voir la proposition signée", "View the signed proposal")} <ArrowRight className="w-3.5 h-3.5" />
                            </a>
                          </p>
                        )}
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <button type="button" onClick={() => setOpened((c) => ({ ...c, [pid]: !c[pid] }))} aria-expanded={isOpen} data-testid={`group-detail-toggle-${pid}`}
                            className="inline-flex items-center gap-1.5 h-11 sm:h-9 px-3 rounded-sm border border-border text-sm font-medium hover:bg-muted transition-colors">
                            {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />} {isOpen ? tr("Masquer le détail", "Hide details") : tr("Voir le détail", "View details")}
                          </button>
                          <a href={`${API}/public/proposals/${pid}/pdf`} target="_blank" rel="noopener noreferrer" data-testid={`group-pdf-${pid}`}
                            className="inline-flex items-center gap-1.5 h-11 sm:h-9 px-3 text-sm font-semibold text-primary hover:underline">
                            <Download className="w-4 h-4" /> PDF
                          </a>
                        </div>
                      </div>
                    </div>
                    {isOpen && (
                      <div className="mt-4 -mx-4 sm:-mx-5 border-t border-border/60 divide-y divide-border/60" data-testid={`group-detail-${pid}`}>
                        {(p.sections || []).map((s) => (
                          <div key={s.id} className="px-4 sm:px-8 py-6">
                            <SectionView section={s} currency={p.currency} clientName={p.client_name} brand={data.brand} />
                          </div>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            {canSign && (
              <section className={`${card} p-4 sm:p-5 space-y-3`} data-testid="group-selection">
                <p className="text-[12px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                  {tr("Votre sélection", "Your selection")} · <span data-testid="group-selected-count">{n}</span> {n > 1 ? tr("propositions", "proposals") : tr("proposition", "proposal")}
                </p>
                {totalCells.length > 0 && (
                  <div className={`grid gap-px bg-border border border-border ${totals.oneShot > 0 && totals.recurring > 0 ? "sm:grid-cols-2" : ""}`}>
                    {totalCells.map((c) => <TotalCell key={c.id} {...c} currency={currency} vatRate={vatRate} ht={HT} />)}
                  </div>
                )}
                <p className="text-xs text-muted-foreground" data-testid="group-vat-mention">
                  {vatFree ? tr("Prix nets, sans TVA.", "Net prices, no VAT.") : tr("Tarifs hors taxes — TVA applicable en sus au taux en vigueur.", "Prices exclude VAT — VAT is added at the applicable rate.")}
                </p>
              </section>
            )}

            {docRows.required.length > 0 && (
              <section className={`${card} overflow-hidden`} data-testid="group-docs">
                <DocumentsBlock endpoint={`/public/groups/${groupId}/documents`} requiredDocuments={docRows.required} documents={docRows.present} onUploaded={reload} />
                <p className="px-5 sm:px-12 pb-6 -mt-4 text-xs text-muted-foreground">{tr("Déposées une seule fois, elles sont rattachées à chacune des propositions concernées.", "Uploaded once, they are attached to each proposal concerned.")}</p>
              </section>
            )}

            {(terms.length > 0 || providerList.length > 0 || consumerLegal) && (
              <section className={`${card} overflow-hidden`} data-testid="group-terms">
                {terms.map((t, i) => (
                  <TermsBlock key={t.id} terms={t} translation={i === 0 ? data.terms_translation : null}
                    pdfHref={firstUsing(t) ? `${API}/public/proposals/${firstUsing(t).public_id}/pdf` : undefined} publicId={firstUsing(t)?.public_id} />
                ))}
                {providerList.length > 0 && <ProvidersBlock providers={providerList} />}
                {consumerLegal && <WithdrawalBlock legal={consumerLegal} onOpenForm={() => setWithdrawalOpen(true)} />}
              </section>
            )}

            <p className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground text-center" data-testid="group-secure">
              <ShieldCheck className="w-3.5 h-3.5 shrink-0" /> {tr("Document sécurisé", "Secure document")} · {brandName}
              <span aria-hidden>·</span>
              <PrivacyNotice companyName={brandName} testIdSuffix="-group" className="py-3 -my-3 underline decoration-dotted underline-offset-2 hover:text-foreground transition-colors" />
            </p>
          </main>

          {canSign && !sheetOpen && (
            <div className="fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-border px-4 py-3" data-testid="group-bar">
              <div className="max-w-3xl mx-auto flex flex-wrap items-center gap-x-4 gap-y-2">
                <p className="flex-none max-w-full text-sm font-semibold">{amountsLine || tr("Aucune proposition sélectionnée", "No proposal selected")}</p>
                <button type="button" onClick={() => setSheetOpen(true)} disabled={n === 0} data-testid="group-sign-open"
                  className="flex-1 min-w-[12rem] h-12 rounded-sm bg-primary text-white font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {tr(`Signer ${n} proposition${n > 1 ? "s" : ""}`, `Sign ${n} proposal${n > 1 ? "s" : ""}`)}
                </button>
              </div>
            </div>
          )}

          {sheetOpen && (
            <GroupSignatureSheet villas={chosen} terms={terms} brandName={brandName} premium={premium} groupId={groupId} deciding={deciding}
              onClose={() => setSheetOpen(false)} onSign={sign} onDocsUploaded={reload} requiredDocuments={docRows.required} documents={docRows.present}
              amountsLine={amountsLine} ht={HT} titles={Object.fromEntries(members.map((p) => [p.public_id, p.title]))} />
          )}
          {withdrawalOpen && consumerLegal?.withdrawal_form && <WithdrawalFormDialog text={consumerLegal.withdrawal_form} onClose={() => setWithdrawalOpen(false)} />}
        </div>
      </ProposalThemeContext.Provider>
    </LangContext.Provider>
  );
}
